import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Select,
  TextField,
  Typography
} from '@mui/material';
import {
  CustomFieldActionColumn,
  CustomFieldColumn,
  CustomFieldHeader,
  CustomFieldHeaderColumn,
  CustomFieldRow
} from './styled';
import Icons from '../../components/icons';
import CustomMenuItem from '../../components/CustomMenuItem/CustomMenuItem';

type MappingItem = { key: string; mappedValue: string };
type MappingArray = MappingItem[];
type MappingObject = Record<string, string>;
type Option = { label: string; value: string };

interface CustomFieldMappingProps {
  onChange: (newValue: MappingArray | MappingObject) => void;
  keyLabel?: string;
  valueLabel?: string;
  addButtonLabel?: string;
  keyOptions?: Option[] | null;
  valueOptions?: Option[] | null;
  formData?: any;
  path?: string;
  isSourceAnObject?: boolean; // <-- new prop to indicate if source is object
}

function getNestedValueFromPath(sourceObject: any, pathString: string): any {
  if (!sourceObject || !pathString) return undefined;
  return pathString
    .split('.')
    .reduce((current, key) => current?.[key], sourceObject);
}

const normalizeSourceData = (sourceValue: any): MappingArray => {
  const isObject =
    typeof sourceValue === 'object' &&
    !Array.isArray(sourceValue) &&
    sourceValue !== null;

  if (isObject) {
    return Object.entries(sourceValue).map(([key, mappedValue]) => ({
      key,
      mappedValue: String(mappedValue)
    }));
  }

  return Array.isArray(sourceValue) ? sourceValue : [];
};

const formatOutputValue = (
  mappings: MappingArray,
  isSourceAnObject: boolean
): MappingArray | MappingObject => {
  const nonEmptyMappings = mappings.filter(
    (mapping) => mapping.key || mapping.mappedValue
  );

  if (isSourceAnObject) {
    return nonEmptyMappings.reduce((obj, { key, mappedValue }) => {
      if (key) {
        obj[key] = mappedValue;
      }
      return obj;
    }, {} as MappingObject);
  }

  return nonEmptyMappings;
};

interface MappingInputProps {
  value: string;
  onChange: (event: any) => void;
  options: Option[] | null | undefined;
  placeholder?: string;
}

const MappingInput = React.memo(
  ({ value, onChange, options, placeholder = 'Select' }: MappingInputProps) => {
    if (options) {
      return (
        <Select
          fullWidth
          value={value}
          onChange={onChange}
          variant='outlined'
          displayEmpty
        >
          <CustomMenuItem value=''>
            <em>{placeholder}</em>
          </CustomMenuItem>
          {options.map((opt) => (
            <CustomMenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </CustomMenuItem>
          ))}
        </Select>
      );
    }
    return (
      <TextField
        fullWidth
        value={value}
        onChange={onChange}
        variant='outlined'
      />
    );
  }
);

const CustomFieldMapping = ({
  onChange,
  keyLabel,
  valueLabel,
  addButtonLabel,
  keyOptions,
  valueOptions,
  formData,
  path,
  isSourceAnObject // <-- new prop
}: CustomFieldMappingProps) => {
  const initialValue = getNestedValueFromPath(formData, path ?? '');

  // If isSourceAnObject is provided, use it; otherwise, infer from initialValue
  const isObjectSource = useMemo(
    () =>
      typeof isSourceAnObject === 'boolean'
        ? isSourceAnObject
        : typeof initialValue === 'object' &&
          !Array.isArray(initialValue) &&
          initialValue !== null,
    [isSourceAnObject, initialValue]
  );

  const [mappings, setMappings] = useState<MappingArray>(
    normalizeSourceData(initialValue)
  );

  useEffect(() => {
    setMappings(normalizeSourceData(initialValue));
  }, [initialValue]);

  const updateAndNotify = useCallback(
    (updatedMappings: MappingArray) => {
      setMappings(updatedMappings);
      const outputValue = formatOutputValue(updatedMappings, isObjectSource);
      onChange(outputValue);
    },
    [isObjectSource, onChange]
  );

  const handleItemChange = (
    index: number,
    field: 'key' | 'mappedValue',
    newValue: string
  ) => {
    const nextMappings = mappings.map((item, i) =>
      i === index ? { ...item, [field]: newValue } : item
    );
    updateAndNotify(nextMappings);
  };

  const handleAddItem = () => {
    const nextMappings = [...mappings, { key: '', mappedValue: '' }];
    setMappings(nextMappings);
  };

  const handleDeleteItem = (index: number) => {
    const nextMappings = mappings.filter((_, i) => i !== index);
    updateAndNotify(nextMappings);
  };

  return (
    <Box style={{ width: '100%' }}>
      <CustomFieldHeader>
        <CustomFieldHeaderColumn>
          <Typography variant='subtitle2'>{keyLabel}</Typography>
        </CustomFieldHeaderColumn>
        <CustomFieldHeaderColumn>
          <Typography variant='subtitle2'>{valueLabel}</Typography>
        </CustomFieldHeaderColumn>
        <Box width='48px' />
      </CustomFieldHeader>

      {mappings.map((item, index) => (
        <CustomFieldRow key={index}>
          <CustomFieldColumn>
            <MappingInput
              value={item.key}
              onChange={(e) => handleItemChange(index, 'key', e.target.value)}
              options={keyOptions}
            />
          </CustomFieldColumn>
          <CustomFieldColumn>
            <MappingInput
              value={item.mappedValue}
              onChange={(e) =>
                handleItemChange(index, 'mappedValue', e.target.value)
              }
              options={valueOptions}
            />
          </CustomFieldColumn>
          <CustomFieldActionColumn>
            <IconButton
              onClick={() => handleDeleteItem(index)}
              aria-label={`Delete mapping for ${
                item.key || `row ${index + 1}`
              }`}
            >
              <Icons.Delete />
            </IconButton>
          </CustomFieldActionColumn>
        </CustomFieldRow>
      ))}

      <Box marginTop={2}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleAddItem}
          startIcon={<Icons.PlusWhite />}
        >
          {addButtonLabel}
        </Button>
      </Box>
    </Box>
  );
};

CustomFieldMapping.defaultProps = {
  keyLabel: 'Field Name',
  valueLabel: 'Field Value',
  addButtonLabel: 'Add Field',
  keyOptions: null,
  valueOptions: null,
  formData: undefined,
  path: undefined,
  isSourceAnObject: undefined // <-- default to undefined
};

export default React.memo(CustomFieldMapping);
